import { useEffect, useRef, useState } from "react";
import Style from "./style.module.css";
import { Link } from "react-router";
import { useApiCall } from "../../../../../custom-hooks/api-call";
import DataNotFound from "../../../../../ui/loader/data-not-found";
import InnerLoader from "../../../../../ui/loader/inner-loader";
import Avatar from "../../../../../ui/avatar";
import { BellRing, Briefcase, ClipboardList, Activity, FolderKanban, Home, TrendingUp, UserCheck, Users, FileText } from "lucide-react";
import useNav from "../../../../../../script/tabingNavArray";

const pagesLable = [
  {
    title: "My Space",
    items: [
      { label: "My Leads", icon: <Users size={'1em'} />, url: '/leads/my-leads/overview' },
      { label: "Account", icon: <Home size={'1em'} />, url: '/accounts/account/overview' },
      { label: "Deals", icon: <Briefcase size={'1em'} />, url: '/deals/deal/overview' },
    ],
  },
  {
    title: "Log & Activity",
    items: [
      { label: "Logs", icon: <ClipboardList size={'1em'} />, url: "/logs/log" },
      { label: "Activity Overview", icon: <Activity size={'1em'} />, url: "/activites/activity/overview" },
      { label: "Follow-Ups", icon: <BellRing size={'1em'} />, url: "/activites/activity/activity-list" },
    ],
  },
  {
    title: "Project & Sale",
    items: [
      { label: "Projects", icon: <FolderKanban size={'1em'} />, url: '/projects/project' },
      { label: "Sales", icon: <TrendingUp size={'1em'} />, url: '/sales/sale/overview' },
      { label: "Assigned Sales", icon: <UserCheck size={'1em'} />, url: '/sales/sale/assigned-sale-list' },
    ],
  },
];

const SearchPopup = ({ isOpen, onClose }) => {
  const useNavs = useNav();
  const apiCall = useApiCall();
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef();
  const [filtered, setFiltered] = useState([]);
  const [loader, setLoader] = useState(false);
  const [defaultState, setDefaultState] = useState(true);
  const [count, setCount] = useState(null);
  const [navResults, setNavResults] = useState([]);

  const callToApi = async (searchKey) => {
    setLoader(true);
    setCount(null)
    try {
      const apiData = JSON.stringify({ search_text: searchKey });
      const reps = await apiCall({ method: 'post', url: 'generic-search', data: apiData });
      setFiltered(reps?.search_text_result?.slice(0, searchKey ? reps?.search_text_result?.length : 10) || []);
      setCount(reps?.search_count);
    } catch (error) {
      console.error("API Error:", error);
      setFiltered([]);
      setCount(null);
    } finally {
      setLoader(false);
    }
  };

  const normalizeNavData = (navs) => {
    if (!navs) return [];

    return Object.entries(navs).flatMap(([section, items]) =>
      items.map(item => ({
        type: "nav",
        section,
        label: item.name,
        url: item.url,
        icon: item.icon,
        activeKey: item.activeKey,
      }))
    );
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const trimmed = searchTerm.trim().toLowerCase();

      // If less than 3 characters → show default screen
      if (trimmed.length < 3) {
        setDefaultState(true);
        setFiltered([]);
        setNavResults([]);
        setCount(null);
        setLoader(false);
        return;
      }

      // If 3 or more characters → show search result
      setDefaultState(false);

      // 🔍 NAV SEARCH
      const navData = normalizeNavData(useNavs);
      const navFiltered = navData.filter(item =>
        item.label.toLowerCase().includes(trimmed)
      );
      setNavResults(navFiltered);

      // 🔍 API SEARCH
      callToApi(trimmed);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, useNavs]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSearchTerm('');
        setDefaultState(true);
        onClose();
      };
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const clos = () => {
    setSearchTerm('');
    setDefaultState(true);
    onClose();
  }

  const formatSegment = (seg) => {
    return seg
      // remove route params like :id
      .replace(/^:.*$/, '')
      // remove numeric ids (123, 45, etc.)
      .replace(/^\d+$/, '')
      // camelCase / PascalCase → space
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      // kebab-case → space
      .replace(/-/g, ' ')
      // Title Case
      .replace(/\b\w/g, char => char.toUpperCase())
      .trim();
  };

  if (!isOpen) return null;

  return (
    <div className={`${Style.search_backdrop}`} onClick={() => clos()}>
      <div className={`${Style.search_container}`} onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className={Style.searchInput}>
          <label htmlFor="searchInputRef" className={Style.searchIcon}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="7" strokeWidth="2" />
              <path d="M21 21l-4.3-4.3" strokeWidth="2" />
            </svg>
          </label>
          <input
            id="searchInputRef"
            type="text"
            ref={searchInputRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search pages or people by name, email, phone?"
            className={Style.search_input}

            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            name="no-autofill"   // important for Chrome
          />

          <div className={Style.search_closed} onClick={() => { onClose() }}>esc</div>
        </div>

        <div className={Style.search_body}>
          {
            defaultState ? (
              <div className={Style.defaulState}>
                <div className={'grid grid-cols-2 gap-6'}>
                  {pagesLable?.map((sec) => (
                    <div key={`GG_${sec.title}`} className="col-span-1">
                      <h4 className="text-xs font-semibold tracking-wider text-gray-500 mb-3">
                        {sec.title.toUpperCase()}
                      </h4>
                      <div className="flex flex-wrap !gap-2">
                        {sec.items.map((item) => (
                          <Link
                            to={item?.url}
                            key={`XX_${item?.label}`}
                            className="flex items-center gap-3 w-full p-2 rounded-lg text-[#006bd6] border border-[#e8eaee80] bg-[#f6f7f866] hover:bg-[#ebf5ff] hover:border-[#66b2ff] transition"
                          >
                            <span className="text-blue-400 text-lg">{item?.icon}</span>
                            <span className="text-sm font-medium">{item?.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className={Style.userList}>
                <div className={Style.search_list}>
                  {!loader ? (
                    (filtered?.length > 0 || navResults.length > 0) ? (
                      <>
                        {/* NAV RESULTS FIRST */}
                        {navResults.length > 0 && (
                          <>
                            <h4 className={Style.sectionTitle}>Navigation</h4>
                            {navResults.map((item, idx) => (
                              <Link
                                key={`nav-${idx}`}
                                to={item.url}
                                onClick={clos}
                                className={Style.search_item}
                              >
                                <span
                                  className="text-blue-400 flex items-center justify-center w-[35px] h-[35px] !text-xl [&>svg]:!w-[1.25em] [&>svg]:!h-[1.25em] [&>svg]:max-w-[100%] [&>svg]:max-h-[100%]"
                                >
                                  {item.icon ? item.icon : <FileText size="1em" />}
                                </span>

                                <div className={Style.cont}>
                                  <h6>{item.label}</h6>
                                  {/* <p><span>page</span></p> */}
                                  <div className={Style.tags}>
                                    {item.url
                                      ?.split('/')
                                      .filter(Boolean)
                                      .map(formatSegment)
                                      .filter(Boolean) // removes empty ones after ID removal
                                      .map((seg, idx) => (
                                        <div key={idx} className={Style.tag}>
                                          {seg}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </Link>
                            ))}
                          </>
                        )}

                        {/* API RESULTS */}
                        {filtered.map((item, idx) => (
                          <div key={idx} className={Style.search_item}>
                            <Avatar
                              width={50}
                              height={50}
                              src={item.user_profile_picture}
                              title={item?.user_name}
                            />

                            <div className={Style.cont}>
                              <h6>
                                {item.user_name}
                                <div
                                  className={`${item?.emp_status === 'Active'
                                    ? Style.active
                                    : Style.inActive
                                    } ${Style.status}`}
                                >
                                  <b />
                                </div>
                              </h6>

                              <h5>{item.user_designation}</h5>
                              <p><span>{item.user_department_name}</span></p>

                              <div className={Style.tags}>
                                {item?.tags?.map((ele, idex) => (
                                  <div key={`${idx}-${idex}`} className={Style.tag}>
                                    {ele}
                                  </div>
                                ))}
                                <a
                                  href={`mailto:${item?.user_email}`}
                                  className={Style.tagEmail}
                                >
                                  {item?.user_email
                                    ? `@${item.user_email.split('@')[0]}`
                                    : '-'}
                                </a>
                              </div>

                              <div className={Style.detailIcon}>
                                <a
                                  href={`mailto:${item?.user_email}`}
                                  className={Style.detailAction}
                                >
                                  📧
                                </a>

                                <Link
                                  target="_blank"
                                  to={`/organization/employee-directory/${item?.user_id}`}
                                  className={Style.detailAction}
                                >
                                  👤
                                </Link>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <DataNotFound title="Result" className={Style.noDataFound} />
                      </div>
                    )
                  ) : (
                    <InnerLoader />
                  )}
                </div>

                {/* NAV SEARCH RESULTS */}
                {/* {navResults.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-xs font-semibold tracking-wider text-gray-500 mb-2">
                      NAVIGATION
                    </h4>

                    <div className="flex flex-col gap-2">
                      
                    </div>
                  </div>
                )} */}
              </div>
            )
          }
        </div>
        <div className={Style.search_footer}>
          {count !== null ? `Result: ${count}` : ""}
        </div>
      </div>
    </div>
  );
};

export default SearchPopup;